/**
 * Pod names a reader can tell apart at a glance: « Odoo · grj7s » rather
 * than `prj-odooperf2-dev-5df54b5dd8-grj7s`, « PostgreSQL 2 » rather than
 * `prj-odooperf2-dev-db-2`.
 *
 * Shown where a pod is named among the others of its environment — the pods
 * card's rows, the logs card's container picker, the upload card's pod
 * picker — every one of them keeping the full name within reach (accessible
 * name, tooltip, option description): it is the pod's identity, the one
 * ArgoCD, `kubectl` and Grafana know it by. The terminal panel's title keeps
 * the full name alone.
 *
 * Read from the names the platform's charts give (helm/charts
 * `waadoo-common`), in an environment whose namespace is
 * `prj-<project>-<branch>`, through the type's descriptor (`PodNaming`,
 * genericity plan, step 6):
 * - the chart's release is the namespace, its fullname the release, or
 *   `<release><chart.fullnameSuffix>` when the release does not contain the
 *   chart's name (`waadoo-common.fullname`) — `<namespace>-odoo` in older
 *   Odoo projects —; the main component's Deployment is the fullname
 *   itself: pods `<fullname>-<replicaset hash>-<pod id>`, named by the main
 *   component's label;
 * - another `app` component's Deployment `<fullname>-<id>`, named by its
 *   label (the platform's convention, no chart of the platform has one yet);
 * - the CNPG cluster `<fullname>-db`: instances `<fullname>-db-<n>`, and
 *   the pods of a pooler `<fullname>-db-pooler-…-<hash>-<id>` (or any
 *   `…-pooler-…` Deployment of the release);
 * - Valkey, the Deployment `<fullname>-valkey` (waadoo-common 0.4.0;
 *   `<fullname>-redis` before it, still running in the releases not yet
 *   migrated); MariaDB (helm/charts
 *   waadoo-common 0.2.0, `_mariadb.tpl`), the StatefulSet
 *   `<fullname>-mariadb` of one replica — pod `<fullname>-mariadb-0` — and
 *   the PreSync Job `<fullname>-mariadb-init` creating its Secret, pods
 *   `<fullname>-mariadb-init-<5>`: a pod of the library's, so the core's,
 *   auxiliary like a module's (`isAuxiliaryPod`) and never a default pod
 *   (`defaultAppPod`);
 * - the roles a type's module contributes (`AppTypeModule.pods`): Odoo's
 *   shell and init Job;
 * - the odoo-project skeleton's email catcher is the Deployment `mailpit`,
 *   a fixed name with no release in it.
 * Anything else is named as it is, the namespace prefix dropped: no role is
 * guessed for a pod these rules do not describe.
 *
 * Matched by NAME. ArgoCD exposes a pod's labels in its resource-tree
 * (`networkingInfo.labels`), but `argocd/pods.ts` does not read them yet:
 * matching `components[].podSelector` against them is a follow-up.
 */

import { useMemo } from 'react';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import type { AppTypeDescriptor } from '@internal/plugin-waadoo-config-common';
import type { PodInfo } from '../argocd/pods';
import { usePodRoleRules } from '../appModules/useAppTypeModules';
import { fallbackAppTitle } from '../shared/appTitle';
import {
  useCurrentAppDescriptor,
  useCurrentAppType,
} from '../shared/AppTypeContext';
import { projectEnvironmentsTranslationRef } from './translation';

/**
 * A role a type's module gives some pods of its chart (Odoo's shell, its
 * init Job), tested on the name after the release, before the main
 * component's Deployment.
 */
export interface PodRoleRule {
  /** `FriendlyPodName.role`; not one of the core's. */
  role: string;
  /** On the name after `<fullname>-`; its first group is the short id. */
  pattern: RegExp;
  /** The name to show, in the reader's language. */
  label: string;
  /**
   * Never the default pod of an action (`defaultAppPod`); `lastResort`:
   * the Logs tab's last resort, after the application's pods.
   */
  defaultPick: 'never' | 'lastResort';
  /**
   * Serves no traffic and is no destination: no version badge, never
   * offered to an upload (`isAuxiliaryPod`).
   */
  auxiliary: boolean;
}

/** What names an environment's pods: its type's chart and components. */
export interface PodNaming {
  /** `chart.fullnameSuffix` (`-odoo`); empty without a descriptor. */
  fullnameSuffix: string;
  /** The main component's id, when the descriptor has one. */
  mainComponent?: string;
  /** The main component's label, else the type's title. */
  mainLabel: string;
  /** The other `app` components, in the descriptor's order. */
  components: ReadonlyArray<{ id: string; label: string }>;
  /** The MariaDB component's label, when the type has one. */
  mariadbLabel?: string;
  /** The type module's roles, in its order. */
  rules: readonly PodRoleRule[];
}

/**
 * The naming of a type's pods: from `descriptor`, else the type's name
 * alone (`fallbackLabel`), and the roles of its module.
 */
export function podNamingOf(
  descriptor: AppTypeDescriptor | undefined,
  fallbackLabel: string,
  rules: readonly PodRoleRule[] = [],
): PodNaming {
  if (!descriptor) {
    return {
      fullnameSuffix: '',
      mainLabel: fallbackLabel,
      components: [],
      rules,
    };
  }
  const main = descriptor.mainComponent;
  return {
    fullnameSuffix: descriptor.chart.fullnameSuffix,
    ...(main ? { mainComponent: main.id } : {}),
    mainLabel: main?.label ?? descriptor.title,
    components: descriptor.components
      .filter(component => component.kind === 'app' && component !== main)
      .map(component => ({ id: component.id, label: component.label })),
    ...(descriptor.mariadbComponent
      ? { mariadbLabel: descriptor.mariadbComponent.label }
      : {}),
    rules,
  };
}

/** The roles of the core's rules (`friendlyPodName`). */
export type CorePodRole =
  | 'app'
  | 'postgres'
  | 'pooler'
  | 'cache'
  | 'mariadb'
  | 'mariadbInit'
  | 'mailpit'
  | 'other';

/**
 * The core's roles of pods that serve no traffic and end on their own, as a
 * module's auxiliary `never` rule: MariaDB's init Job.
 */
const CORE_AUXILIARY_ROLES: ReadonlySet<string> = new Set<CorePodRole>([
  'mariadbInit',
]);

export interface FriendlyPodName {
  /** A {@link CorePodRole}, or the role of a module's rule (`shell`). */
  role: string;
  /**
   * The name to show, never empty: a component's label or a product name
   * (left untranslated), « PostgreSQL <n> » for a database instance and
   * « <MariaDB's label> init Job » for MariaDB's init Job — their default
   * forms, the translated ones are {@link usePodNames}' business —, a module
   * rule's label, or, for `other`, the name without its namespace prefix (the
   * full name when it has none).
   */
  label: string;
  /**
   * The pod's own random suffix, what tells two replicas of a Deployment
   * apart. Absent for a database instance, which its number tells apart, and
   * for `other`, whose label is the whole name already.
   */
  shortId?: string;
  /** The CNPG instance number, for `postgres` only. */
  instance?: number;
  /** The descriptor's component, for `app` (absent without a descriptor). */
  component?: string;
}

/**
 * The two random segments a Deployment's pod ends with: the ReplicaSet's
 * pod-template hash (10 characters at most), then the 5 characters
 * `generateName` adds for the pod. Neither holds a dash. The suffix's fixed
 * length is what keeps a StatefulSet's pod of the release — `<release>-cron-0`
 * — from reading as the main Deployment's.
 */
const DEPLOYMENT_POD = /^([a-z0-9]{1,10})-([a-z0-9]{5})$/;

/** The last dash-separated segment of a name: a Deployment pod's own id. */
function lastSegment(name: string): string {
  return name.slice(name.lastIndexOf('-') + 1);
}

/** See the module header for the naming this reads. */
export function friendlyPodName(
  name: string,
  options: { namespace: string; naming: PodNaming },
): FriendlyPodName {
  const { naming } = options;
  const prefix = `${options.namespace}-`;
  // Kubernetes names never end with a dash: something always follows the
  // prefix, so the stripped name is never empty.
  const stripped = name.startsWith(prefix)
    ? name.slice(prefix.length)
    : undefined;
  const other: FriendlyPodName = { role: 'other', label: stripped ?? name };

  // Fixed name, no release in it.
  if (/^mailpit-[a-z0-9]+-[a-z0-9]+$/.test(name)) {
    return { role: 'mailpit', label: 'Mailpit', shortId: lastSegment(name) };
  }
  // The rules below are about the release's objects: a pod outside the
  // namespace's prefix is none of them.
  if (stripped === undefined) {
    return other;
  }
  // The fullname `<release><fullnameSuffix>` (a release without the chart's
  // name). Dropped only when something follows it: a pod named
  // `<namespace>-odoo` alone is `other`.
  const chartName = naming.fullnameSuffix.replace(/^-/, '');
  const rest =
    chartName !== '' && stripped.startsWith(`${chartName}-`)
      ? stripped.slice(chartName.length + 1)
      : stripped;

  const instance = /^db-(\d+)$/.exec(rest);
  if (instance) {
    const n = Number(instance[1]);
    return { role: 'postgres', label: `PostgreSQL ${n}`, instance: n };
  }
  // `pooler` as a whole segment, any segments after it (`-rw`), then the
  // Deployment's two.
  if (/(^|-)pooler(-[a-z0-9]+)*-[a-z0-9]+-[a-z0-9]+$/.test(rest)) {
    return { role: 'pooler', label: 'PgBouncer', shortId: lastSegment(rest) };
  }
  // Both names during the alias period (header).
  if (/^(valkey|redis)-[a-z0-9]+-[a-z0-9]+$/.test(rest)) {
    return { role: 'cache', label: 'Valkey', shortId: lastSegment(rest) };
  }
  if (naming.mariadbLabel !== undefined) {
    if (/^mariadb-\d+$/.test(rest)) {
      return { role: 'mariadb', label: naming.mariadbLabel };
    }
    const init = /^mariadb-init-([a-z0-9]{5})$/.exec(rest);
    if (init) {
      return {
        role: 'mariadbInit',
        label: `${naming.mariadbLabel} init Job`,
        shortId: init[1],
      };
    }
  }
  // The module's roles, tested before the Deployments, whose pattern some
  // would match too (an init Job's `init-<5>` reads as a pod-template hash
  // and a pod id).
  for (const rule of naming.rules) {
    const match = rule.pattern.exec(rest);
    if (match) {
      return {
        role: rule.role,
        label: rule.label,
        ...(match[1] ? { shortId: match[1] } : {}),
      };
    }
  }
  for (const component of naming.components) {
    const pod = new RegExp(
      `^${component.id}-[a-z0-9]{1,10}-([a-z0-9]{5})$`,
    ).exec(rest);
    if (pod) {
      return {
        role: 'app',
        label: component.label,
        shortId: pod[1],
        component: component.id,
      };
    }
  }
  const deployment = DEPLOYMENT_POD.exec(rest);
  if (deployment) {
    return {
      role: 'app',
      label: naming.mainLabel,
      shortId: deployment[2],
      ...(naming.mainComponent ? { component: naming.mainComponent } : {}),
    };
  }
  return other;
}

/**
 * The naming of the pods of the view's project (`AppTypeProvider`): its
 * descriptor and its module's roles. Without a provider, the embedded Odoo
 * descriptor's (`useCurrentAppDescriptor`).
 */
export function useCurrentPodNaming(): PodNaming {
  return usePodNaming(useCurrentAppType(), useCurrentAppDescriptor());
}

/**
 * The naming of the pods of a project of `appType` whose descriptor is
 * `descriptor` (`useAppTypeDescriptor`), for a caller outside its
 * `AppTypeProvider` (the branch page itself).
 */
export function usePodNaming(
  appType: string | undefined,
  descriptor: AppTypeDescriptor | undefined,
): PodNaming {
  const rules = usePodRoleRules(appType);
  return useMemo(
    () => podNamingOf(descriptor, fallbackAppTitle(appType), rules),
    [descriptor, appType, rules],
  );
}

/** A pod's friendly name, in the page's words. */
export interface PodNameText {
  /** The translated label: « PostgreSQL 2 », « Odoo »… */
  label: string;
  shortId?: string;
  /** Label and short id on one line, « Odoo · grj7s », for the pickers. */
  text: string;
}

/**
 * {@link friendlyPodName} translated: the PostgreSQL instance's format and
 * the one-line form the pickers show come from the card's messages (a
 * module rule's label is its module's, translated already). Only the pod's
 * name and namespace are read: the « À surveiller » card names the pod of
 * an event, which is no `PodInfo`.
 */
export function usePodNames(
  naming: PodNaming,
): (pod: Pick<PodInfo, 'name' | 'namespace'>) => PodNameText {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  return pod => {
    const friendly = friendlyPodName(pod.name, {
      namespace: pod.namespace,
      naming,
    });
    let label = friendly.label;
    if (friendly.instance !== undefined) {
      label = t('podName.postgres', { instance: String(friendly.instance) });
    } else if (
      friendly.role === 'mariadbInit' &&
      naming.mariadbLabel !== undefined
    ) {
      label = t('podName.mariadbInit', { label: naming.mariadbLabel });
    }
    return {
      label,
      shortId: friendly.shortId,
      text: friendly.shortId
        ? t('podName.withId', { label, id: friendly.shortId })
        : label,
    };
  };
}

/** The module rule `pod` falls under, if any. */
function ruleOf(
  pod: Pick<PodInfo, 'name' | 'namespace'>,
  naming: PodNaming,
): PodRoleRule | undefined {
  const { role } = friendlyPodName(pod.name, {
    namespace: pod.namespace,
    naming,
  });
  return naming.rules.find(rule => rule.role === role);
}

/** A pod of one of {@link CORE_AUXILIARY_ROLES}: MariaDB's init Job. */
function isCoreAuxiliary(
  pod: Pick<PodInfo, 'name' | 'namespace'>,
  naming: PodNaming,
): boolean {
  return CORE_AUXILIARY_ROLES.has(
    friendlyPodName(pod.name, { namespace: pod.namespace, naming }).role,
  );
}

/**
 * A pod that serves no traffic: of a module's auxiliary role
 * (`PodRoleRule.auxiliary`, Odoo's shell) or of the core's (MariaDB's init
 * Job). No version badge, never an upload's destination.
 */
export function isAuxiliaryPod(
  pod: Pick<PodInfo, 'name' | 'namespace'>,
  naming: PodNaming,
): boolean {
  return (
    (ruleOf(pod, naming)?.auxiliary ?? false) || isCoreAuxiliary(pod, naming)
  );
}

/**
 * The pod an action on the environment's application (its logs, an upload
 * to its files) targets by default: a pod of the main component first;
 * otherwise the first other `app` pod, then the first pod at all. Never a
 * pod of a module's role (`PodRoleRule.defaultPick`: Odoo's shell, which
 * serves no traffic and stops on its own, and its init Job), unless its
 * pick is `lastResort` and `initAsLastResort` (the Logs tab): then it comes
 * right after the application pods — on a first deployment, before the
 * application runs, the init Job's logs are the ones being looked for — and
 * before the database and the tools. Never a pod of the core's auxiliary
 * roles either (MariaDB's init Job), not even as a last resort. Every pod
 * stays pickable by hand where the list offers it.
 */
export function defaultAppPod<
  T extends Pick<PodInfo, 'name' | 'namespace' | 'category'>,
>(
  pods: T[],
  naming: PodNaming,
  options: { initAsLastResort?: boolean } = {},
): T | undefined {
  const nameOf = (pod: T) =>
    friendlyPodName(pod.name, { namespace: pod.namespace, naming });
  const candidates = pods.filter(
    pod => !ruleOf(pod, naming) && !isCoreAuxiliary(pod, naming),
  );
  const lastResort = options.initAsLastResort
    ? pods.find(pod => ruleOf(pod, naming)?.defaultPick === 'lastResort')
    : undefined;
  const isMain = (pod: T) => {
    const friendly = nameOf(pod);
    return (
      friendly.role === 'app' && friendly.component === naming.mainComponent
    );
  };
  return (
    candidates.find(pod => pod.category === 'app' && isMain(pod)) ??
    candidates.find(pod => pod.category === 'app') ??
    lastResort ??
    candidates[0]
  );
}
